import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreatableSelect } from "@/components/Selects/select";
import { useTRPC } from "@/trpc";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { PROFILE_SELECT_CLASS_NAMES } from "@/components/Selects/profile-select-styles";

export function ProviderBoardSpecialtyField({
  specialty,
  taxonomyCode,
  disabled,
  isEditing,
  onChange,
}: {
  specialty: string;
  taxonomyCode: string;
  disabled: boolean;
  isEditing: boolean;
  onChange: (value: { specialty: string; taxonomyCode: string }) => void;
}) {
  const inputId = useId();
  const trpc = useTRPC();
  const catalog = useQuery(
    trpc.healthcareProfessionalTypes.getMedicalSpecialties.queryOptions(),
  );
  const options = (catalog.data ?? []).map((entry) => ({
    label: entry.name,
    value: entry.code,
  }));
  let selected = options.find((entry) =>
    taxonomyCode
      ? entry.value === taxonomyCode
      : entry.label.toLowerCase() === specialty.toLowerCase(),
  );
  if (!selected && (specialty || taxonomyCode)) {
    selected = { label: specialty || taxonomyCode, value: taxonomyCode };
  }

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className={PROFILE_FORM_STYLES.label}>
        Specialty
      </label>
      <CreatableSelect
        inputId={inputId}
        classNames={PROFILE_SELECT_CLASS_NAMES}
        menuPlacement="auto"
        options={options}
        value={selected ?? null}
        isLoading={catalog.isLoading}
        isDisabled={disabled || isEditing}
        isClearable
        placeholder="Search specialties"
        formatCreateLabel={(value) => `Use “${value}”`}
        onCreateOption={(value) =>
          onChange({ specialty: value.trim(), taxonomyCode: "" })
        }
        onChange={(option) =>
          onChange({
            specialty: option?.label ?? "",
            taxonomyCode: option?.value ?? "",
          })
        }
      />
      {isEditing && (
        <p className="text-xs leading-relaxed text-slate-500">
          Identifies this certification. Add a new credential to use a different
          specialty.
        </p>
      )}
      {catalog.isError && !isEditing && (
        <p role="alert" className="text-xs leading-relaxed text-slate-500">
          Specialty search is unavailable. You can enter the specialty shown on
          your certificate.
        </p>
      )}
    </div>
  );
}
